import { Toaster } from "@/components/ui/sonner";
import { userConfigStore } from "@/store/user-config-store";
import { InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, useAccount, useMsal } from "@azure/msal-react";
import { focusManager, MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AxiosError } from "axios";
import { MotionConfig } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AbilityProvider, createForUser } from "./casl-provider";
import { SessionExpiredError } from "./common/errors/session-expired-error";
import { useAppFeatureFlag } from "./common/hooks/use-app-feature-flag";
import { ErrorPage } from "./components/error-page/error-page";
import { Loader, LoaderSize } from "./components/ui/loader";
import { setTokenProvider } from "./config/axios";
import { isDev } from "./config/configuration";
import { FeatureFlag } from "./config/feature-flags";
import { SCOPES } from "./config/msal";
import { isSessionRecoveryMsalError } from "./config/msal-error";
import Sentry from "./config/sentry";
import { useApp } from "./config/use-app";
import { NotificationsRuntime } from "./modules/notification/notifications-runtime";
import { KonamiRuntime } from "./modules/arcade/konami-runtime";
import { Routes } from "./routes";
import { AssetAccessError, getAssetAccessRenewalDelay, userStore } from "./store/user-store";
import { AuthenticationErrorView } from "./views/common/authentication-error.view";
import { MaintenanceView } from "./views/common/maintenance.view";

function refetchOnVisibleAfter(beforeFocus: () => Promise<unknown>) {
  return (handleFocus: (focused?: boolean) => void) => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        handleFocus();
        return;
      }
      void beforeFocus().finally(() => handleFocus());
    };

    window.addEventListener("visibilitychange", onVisibilityChange, false);
    return () => window.removeEventListener("visibilitychange", onVisibilityChange);
  };
}

function LoadingComponent() {
  return <Loader size={LoaderSize.Large} fullscreen />;
}

export function App() {
  const { t } = useTranslation("common");
  const { handleFetchError } = useApp();
  const { showTanstackQueryButton } = userConfigStore();
  const { accounts, instance } = useMsal();
  const account = useAccount(accounts[0] || {});
  const { user, isLoading, assetAccessExpiresAt, loadUserData, renewAssetAccess, renewExpiringAssetAccess } =
    userStore();
  const { value: isMaintenance } = useAppFeatureFlag(FeatureFlag.IsMaintenance);
  const [userDataError, setUserDataError] = useState<Error | null>(null);

  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: 2,
        },
      },
      queryCache: new QueryCache({
        onError: async (error) => handleFetchError(error as AxiosError),
      }),
      mutationCache: new MutationCache({
        onError: async (error) => handleFetchError(error as AxiosError),
      }),
    });
  }, [handleFetchError]);

  useEffect(() => {
    if (isMaintenance || !account || !instance) return;

    const tokenProvider = async () => {
      const [apiTokenResponse, msGraphTokenResponse] = await Promise.all([
        instance.acquireTokenSilent({
          account,
          scopes: SCOPES.API,
        }),
        instance.acquireTokenSilent({
          account,
          scopes: SCOPES.MS_GRAPH,
        }),
      ]);

      return {
        apiToken: apiTokenResponse.accessToken,
        msGraphToken: msGraphTokenResponse.accessToken,
      };
    };

    setTokenProvider(tokenProvider);

    if (!user) {
      loadUserData()
        .then(() => setUserDataError(null))
        .catch((error) => {
          if (error instanceof AssetAccessError || isSessionRecoveryMsalError(error)) {
            return handleFetchError(new SessionExpiredError(error.message));
          }
          Sentry.captureException(error, { extra: { action: "fetch-user-data" } });
          setUserDataError(error instanceof Error ? error : new Error("Failed to load user data"));
        });
    }
  }, [account, instance, user, loadUserData, isMaintenance, handleFetchError]);

  const handleAssetAccessRenewalError = useCallback(
    (error: unknown) => {
      const rawMessage = error instanceof Error ? error.message : String(error);
      const message = rawMessage.replace(/^Unable to establish private asset access: /, "");
      return handleFetchError(new SessionExpiredError(`Unable to renew private asset access: ${message}`));
    },
    [handleFetchError],
  );

  useEffect(() => {
    if (!assetAccessExpiresAt) return;

    const renewalTimer = setTimeout(() => {
      renewAssetAccess().catch(handleAssetAccessRenewalError);
    }, getAssetAccessRenewalDelay(assetAccessExpiresAt));

    return () => clearTimeout(renewalTimer);
  }, [assetAccessExpiresAt, renewAssetAccess, handleAssetAccessRenewalError]);

  useEffect(() => {
    // Browsers pause timers in hidden tabs and during sleep, so the renewal timer can fire after the cookie expired.
    // Renew before the refetch on focus, otherwise the refetched rows render images that CloudFront rejects.
    focusManager.setEventListener(
      refetchOnVisibleAfter(() => renewExpiringAssetAccess().catch(handleAssetAccessRenewalError)),
    );
  }, [renewExpiringAssetAccess, handleAssetAccessRenewalError]);

  if (isMaintenance) {
    return <MaintenanceView />;
  }

  if (userDataError) {
    return (
      <ErrorPage
        errorCode={500}
        message={t("ErrorLoadingUserData")}
        description={t("ErrorLoadingUserDataDescription")}
        hideBackButton={true}
        hideReloadButton={true}
      />
    );
  }

  if (isLoading || (account && !assetAccessExpiresAt)) {
    return <LoadingComponent />;
  }

  return (
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={{ scopes: SCOPES.API }}
          errorComponent={AuthenticationErrorView}
          loadingComponent={LoadingComponent}
        >
          <AbilityProvider value={createForUser(user)}>
            <Routes />
            <Toaster position="top-center" />
            <NotificationsRuntime />
            <KonamiRuntime />
            {showTanstackQueryButton && isDev && <ReactQueryDevtools client={queryClient} initialIsOpen={false} />}
          </AbilityProvider>
        </MsalAuthenticationTemplate>
      </QueryClientProvider>
    </MotionConfig>
  );
}
